import React from 'react';
import { Info, Brain, Recycle, BarChart3, Shield, DollarSign, Users, Leaf } from 'lucide-react';

const Section = ({ title, children, icon }) => (
  <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
    <div className="flex items-center mb-2">
      {icon}
      <h3 className="ml-2 text-lg font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
  </div>
);

const AboutPage = ({ onNavigate }) => {
  return (
    <div className="min-h-[70vh] px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900">About Triage_OS</h1>
          <p className="mt-2 text-gray-600">
            Triage_OS is a cloud-native SaaS platform that empowers manufacturers, OEMs, and asset managers to optimize the lifecycle of industrial assets through AI-driven circular economy strategies. 
            Built on Django (backend) and React (frontend), it combines EU-ready Digital Product Passports (DPP) with AI insights for real-time traceability, predictive maintenance, and data-driven decision-making.
          </p>
        </div>

        <div className="space-y-5">
          <Section title="The Problem" icon={<Info className="w-5 h-5 text-red-600" />}> 
            Linear "take–make–dispose" models lead to waste, data fragmentation, and missed circular opportunities. Transparency is limited, and EU regulations (Ecodesign for Sustainable Products Regulation) will require Digital Product Passports by 2026.
          </Section>

          <Section title="Our Solution" icon={<Brain className="w-5 h-5 text-indigo-600" />}> 
            Triage_OS unifies DPP, AI, and monitoring into a single platform:
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Digital Product Passport (EU-ready):</strong> JSON passports capture identity, materials, lifecycle events, and performance data for compliance and traceability.</li>
              <li><strong>AI-Powered Insights:</strong> Models analyze condition, market volatility, and materials to recommend optimal R‑strategies (Reuse, Repair, Remanufacture, Recycle) and boost ROI.</li>
              <li><strong>Real-Time Monitoring:</strong> Predictive analytics and sensors help prevent failures, extending asset life by 20–40%.</li>
              <li><strong>User-Friendly Interface:</strong> Intuitive dashboard and triage flows for non‑technical teams; role-based access for secure sharing.</li>
              <li><strong>Event-Driven Architecture:</strong> Append‑only audit trails ensure integrity and regulatory compliance.</li>
            </ul>
          </Section>

          <Section title="Competitive Advantage" icon={<Shield className="w-5 h-5 text-purple-600" />}> 
            First mover in integrated DPP + AI for SMEs. Unlike enterprise-heavy suites, Triage_OS is affordable, aligned with EU regulation, and built on open-source foundations for rapid customization. Public/restricted DPP splits protect sensitive data.
          </Section>

          <Section title="Market Opportunity" icon={<BarChart3 className="w-5 h-5 text-cyan-600" />}> 
            Targeting the €200B European industrial asset market within a $2.5T global TAM. The circular economy segment is ~$1.1T today and is projected to reach $4.5T by 2030 (Ellen MacArthur Foundation).
          </Section>

          <Section title="Business Model & Pricing" icon={<DollarSign className="w-5 h-5 text-emerald-600" />}> 
            SaaS subscriptions with tiered plans (Basic: €50/user/month; Pro: €150; Enterprise: custom), plus premium AI modules and DPP implementation consulting. High-margin, scalable unit economics.
          </Section>

          <Section title="Team & Milestones" icon={<Users className="w-5 h-5 text-amber-600" />}> 
            Founder/CEO and CTO are seasoned in asset management and AI, supported by EU regulatory advisors. Milestones: MVP launched, 5 pilot clients in beta, and 20% MoM user growth.
          </Section>

          <Section title="Why Now & Impact" icon={<Leaf className="w-5 h-5 text-green-600" />}> 
            EU DPP deadlines are approaching and ESG investing is surging. Triage_OS directly supports UN SDGs 12 (Responsible Consumption) and 13 (Climate Action) by enabling circular operations and measurable impact.
          </Section>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => onNavigate && onNavigate('management')}
            className="px-5 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-medium shadow"
          >
            Open Asset Manager
          </button>
          <button
            onClick={() => onNavigate && onNavigate('dashboard')}
            className="px-5 py-2.5 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium shadow"
          >
            View Dashboard
          </button>
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            className="px-5 py-2.5 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium shadow"
          >
            Back to Landing
          </button>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          Contact: <span className="underline">[Your Email/Phone]</span> | Demo available upon request
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
